import type { Map as MapboxMap, MapLayerMouseEvent } from 'mapbox-gl';
import { useEffect } from 'react';
import type { MapRef } from 'react-map-gl';
import {
  PIN_RING_KINDS,
  RINGS,
  clusterRingOpacityExpression,
  pinRingOpacityExpression,
  rippleAllowed,
  type MarkerKind,
} from '@/components/Dashboard/markerPulse';

// Test/measurement hook (non-prod builds only), read by e2e/map-ux.spec.ts:
// cost of our own per-frame work and which marker ripples now. `inView` is
// kept for the spec's shape; since 2026-09-20 it counts the markers that COULD
// ripple (hovered or selected), i.e. 0 or 1.
type PulseStats = {
  frames: number;
  totalMs: number;
  maxMs: number;
  inView: number;
  active: number;
  activeIds: number[];
};

// Far enough in the future that the blend-out never starts on its own.
const NEVER_MS = 1e12;

/**
 * Ripples the cluster markers' three rings (`<kind>-ring-0..2`) and a farmer
 * pin's one ring (`<kind>-pin-ring`) from ONE requestAnimationFrame loop.
 *
 * Founder decision 2026-09-20 (staging review): the ripple animates ONLY on the
 * single marker the visitor is pointing at (hover) or has selected (click /
 * tap on a pin), never on a marker of its own accord. SUPERSEDED in place
 * (S2): the 2026-09-17/18 behaviour where one random marker in view rippled
 * for ~2 cycles and handed off to another (markerPulse.ts nextActive,
 * densityFactor - both kept, unit-tested, no longer called from here).
 *
 * Which marker ripples is stored as Mapbox feature-state {start, end} keyed by
 * its cluster_id or (always negative) pulse_id, so the static rings and the
 * rippling one are the same layers. With nothing hovered or selected the loop
 * paints nothing, so an idle map costs no repaints. The loop stops while the
 * map is off screen; with prefers-reduced-motion it never starts and every ring
 * keeps the static opacity it is declared with in Layers.tsx.
 */
export const useMarkerPulse = (
  mapRef: React.RefObject<MapRef | null>,
  // which clustered pin layer is mounted, or null for none
  kind: MarkerKind | null,
) => {
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!kind || !map) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;
    let onScreen = true;
    // The one marker rippling now (hovered wins while the pointer is on a
    // marker; otherwise the selected one, if any).
    let active: number | null = null;
    let hovered: number | null = null;
    let selected: number | null = null;
    let lastPaint = 0;
    // The ripple strength used to fall with the markers in view; with a single
    // pointed-at marker it is always full.
    const DENSITY = 1;
    const stats: PulseStats = {
      frames: 0,
      totalMs: 0,
      maxMs: 0,
      inView: 0,
      active: 0,
      activeIds: [],
    };
    if (import.meta.env.MODE !== 'prod') {
      (window as unknown as { __pulseStats?: PulseStats }).__pulseStats = stats;
    }

    const paint = (now: number) => {
      RINGS.forEach((_, k) => {
        const id = `${kind}-ring-${k}`;
        if (!map.getLayer(id)) return;
        // validate:false - the expressions are built by our own tested code, and
        // style validation on every frame would be the bulk of the JS cost.
        map.setPaintProperty(
          id,
          'circle-stroke-opacity',
          clusterRingOpacityExpression(k, now, DENSITY),
          { validate: false },
        );
      });
      // The pin's one ring follows the same curve as the cluster's inner ring
      // (k=0: no stagger, since there is nothing to stagger against).
      const pinRingId = `${kind}-pin-ring`;
      if (map.getLayer(pinRingId)) {
        map.setPaintProperty(
          pinRingId,
          'circle-stroke-opacity',
          pinRingOpacityExpression(now, DENSITY),
          { validate: false },
        );
      }
    };

    // Applies hovered/selected to the feature state. `now` = rAF clock.
    const apply = (now: number) => {
      const next = rippleAllowed(kind, map.getZoom()) ? hovered ?? selected : null;
      if (next === active) return;
      if (active !== null && map.getSource(kind)) {
        map.removeFeatureState({ source: kind, id: active });
      }
      if (next !== null && map.getSource(kind)) {
        map.setFeatureState({ source: kind, id: next }, { start: now, end: NEVER_MS });
      }
      active = next;
      stats.inView = next === null ? 0 : 1;
      stats.active = stats.inView;
      stats.activeIds = next === null ? [] : [next];
    };

    // Every paint forces a full map repaint, which is the real cost of the
    // ripple (our own JS is ~1 ms). A slow opacity ripple looks the same at
    // 30 fps, so skip every other frame on 60 Hz screens and halve the GPU work.
    const MIN_PAINT_INTERVAL_MS = 32;

    const tick = (now: number) => {
      frame = requestAnimationFrame(tick);
      if (now - lastPaint < MIN_PAINT_INTERVAL_MS) return;
      lastPaint = now;
      const started = performance.now();
      const hadActive = active !== null;
      apply(now);
      // Nothing rippling: no repaint. One last paint when the marker stops,
      // so its rings settle on the static opacities.
      if (active === null && !hadActive) return;
      paint(now);
      const cost = performance.now() - started;
      stats.frames += 1;
      stats.totalMs += cost;
      stats.maxMs = Math.max(stats.maxMs, cost);
    };

    const stop = () => {
      cancelAnimationFrame(frame);
      frame = 0;
    };

    // Back to the static rings: drop the ripple state.
    const clear = () => {
      if (active !== null && map.getSource(kind)) {
        map.removeFeatureState({ source: kind, id: active });
      }
      active = null;
      stats.inView = 0;
      stats.active = 0;
      stats.activeIds = [];
    };

    const sync = () => {
      stop();
      if (reducedMotion.matches) {
        clear();
      } else if (onScreen) frame = requestAnimationFrame(tick);
    };

    // A marker's ripple id: clusters use cluster_id, pins their pulse_id. Tree
    // pins carry no ring (PIN_RING_KINDS), so pointing at one ripples nothing.
    const idOf = (e: MapLayerMouseEvent): number | null => {
      const props = e.features?.[0]?.properties;
      const clusterId = props?.cluster_id;
      if (typeof clusterId === 'number') return clusterId;
      const pulseId = props?.pulse_id;
      if (typeof pulseId === 'number' && PIN_RING_KINDS.includes(kind)) return pulseId;
      return null;
    };
    const onMove = (e: MapLayerMouseEvent) => {
      hovered = idOf(e);
    };
    const onLeave = () => {
      hovered = null;
    };
    const onMarkerClick = (e: MapLayerMouseEvent) => {
      selected = idOf(e);
    };
    // A click on empty map (not on a marker) drops the selection. Mapbox fires
    // the layer click first, then the map click, so `defaultPrevented` on the
    // original event tells them apart.
    const onMapClick = (e: MapLayerMouseEvent) => {
      if (!e.originalEvent.defaultPrevented) selected = null;
    };
    const onMarkerClickMark = (e: MapLayerMouseEvent) => {
      onMarkerClick(e);
      e.originalEvent.preventDefault();
    };
    const layers = [`${kind}-clusters`, `${kind}-marker`];
    for (const layer of layers) {
      map.on('mousemove', layer, onMove);
      map.on('mouseleave', layer, onLeave);
      map.on('click', layer, onMarkerClickMark);
    }
    map.on('click', onMapClick);

    // Pause while the map is scrolled out of view (the project page sits below
    // it). Hidden tabs need no handling: browsers stop rAF there themselves.
    const observer = new IntersectionObserver(([entry]) => {
      onScreen = entry.isIntersecting;
      sync();
    });
    observer.observe(map.getContainer());
    reducedMotion.addEventListener('change', sync);
    sync();

    return () => {
      stop();
      clear();
      observer.disconnect();
      reducedMotion.removeEventListener('change', sync);
      for (const layer of layers) {
        map.off('mousemove', layer, onMove);
        map.off('mouseleave', layer, onLeave);
        map.off('click', layer, onMarkerClickMark);
      }
      map.off('click', onMapClick);
    };
  }, [mapRef, kind]);
};
